import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { useApiClient } from '../../base/hooks/useApiClient';
import { Page } from '../../layout/Page';
import { ArrowLeft, Clock, Calendar, DollarSign, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { Messages } from '../../base/Stenographer';

interface SessionRunDetails {
    id: number;
    startedAt: string;
    endedAt: string | null;
    duration: number | null;
    languageDurations: Record<string, number>;
    messages: Array<{ transcript: string; timestamp: string }>;
    cost: number | null;
    session: {
        name: string;
        originalLanguage: string;
        translationLanguages: string[];
    };
}

export function SessionRunDetailsPage() {
    const { sessionUuid, runId } = useParams<{ sessionUuid: string; runId: string }>();
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [isTranscriptsExpanded, setIsTranscriptsExpanded] = useState(false);

    const { value: runDetails, loading } = useAsync(async () => {
        if (!sessionUuid || !runId) return null;

        const response = await apiClient.get(`sessions/${sessionUuid}/runs/${runId}`);
        if (!response.ok) {
            console.error('Error fetching session run details');
            return null;
        }

        return await response.json() as SessionRunDetails;
    }, [sessionUuid, runId]);

    const calculateBillableDuration = (duration: number | null, languageDurations: Record<string, number>) => {
        if (duration === null) return null;

        const languageDurationValues = Object.values(languageDurations);
        
        if (languageDurationValues.length === 0) {
            return duration;
        }

        const maxLanguageDuration = Math.max(...languageDurationValues);
        const sumOfOtherLanguages = languageDurationValues.reduce((sum, dur) => sum + dur, 0) - maxLanguageDuration;
        
        return duration + sumOfOtherLanguages;
    };

    const formatDuration = (milliseconds: number | null) => {
        if (milliseconds === null) return 'In Progress';
        const seconds = milliseconds / 1000;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const formatDate = (dateString: string) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(dateString));
    };

    if (loading) {
        return (
            <Page>
                <div className="p-6">
                    <div className="text-center py-12">
                        <div className="text-lg text-gray-600">Loading session run details...</div>
                    </div>
                </div>
            </Page>
        );
    }

    if (!runDetails) {
        return (
            <Page>
                <div className="p-6">
                    <div className="text-center py-12">
                        <div className="text-lg text-gray-600">Session run not found</div>
                    </div>
                </div>
            </Page>
        );
    }

    return (
        <Page>
            <div className="p-6 max-w-5xl mx-auto">
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => navigate('/panel/sessions')}
                            className="flex items-center text-indigo-600 hover:text-indigo-700"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Sessions
                        </button>

                        <button
                            onClick={() => navigate(`/panel/sessions/${sessionUuid}`)}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Play className="h-4 w-4 mr-2" />
                            Start New Run
                        </button>
                    </div>

                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Session Run Details
                    </h1>
                    <p className="text-lg text-gray-600">Session {runDetails.session.name}</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Started At</p>
                                <p className="text-lg font-semibold text-gray-900 mt-1">
                                    {formatDate(runDetails.startedAt)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Billable duration</p>
                                <p className="text-lg font-semibold text-gray-900 mt-1">
                                    {formatDuration(calculateBillableDuration(runDetails.duration, runDetails.languageDurations))}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <Clock className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Cost</p>
                                <p className="text-lg font-semibold text-gray-900 mt-1">
                                    {runDetails.cost ? `$${runDetails.cost.toFixed(2)}` : 'N/A'}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="h-6 w-6 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transcripts/Messages */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <button
                        onClick={() => setIsTranscriptsExpanded(!isTranscriptsExpanded)}
                        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <h2 className="text-xl font-semibold text-gray-900">Transcripts</h2>
                        </div>
                        {isTranscriptsExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                    </button>
                    
                    {isTranscriptsExpanded && (
                        <div className="px-6 pb-6">
                            <Messages 
                                messages={runDetails.messages.map(m => m.transcript)} 
                                className="h-[500px]"
                            />
                        </div>
                    )}
                </div>
            </div>
        </Page>
    );
}
